/**
 * CRO Search — Location-aware search overlay
 *
 * Combines two search sources:
 * 1. Local location index (instant, from cro-location-index.js)
 * 2. Shopify Predictive Search API (async, for product results)
 *
 * Dependencies: cro-location-index.js must be loaded first.
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     DOM references
     ----------------------------------------------------------------------- */
  var overlay   = document.getElementById('CroSearchOverlay');
  var input     = document.getElementById('CroSearchInput');
  var form      = document.getElementById('CroSearchForm');
  var closeBtn  = document.getElementById('CroSearchClose');
  var bodyEl    = document.getElementById('CroSearchBody');
  var defaultEl = document.getElementById('CroSearchDefault');
  var resultsEl = document.getElementById('CroSearchResults');
  var emptyEl   = document.getElementById('CroSearchEmpty');

  if (!overlay || !input || !defaultEl || !resultsEl || !emptyEl) return;

  /* -----------------------------------------------------------------------
     State
     ----------------------------------------------------------------------- */
  var debounceTimer = null;
  var currentQuery  = '';
  var focusedIndex  = -1;
  var allResultLinks = [];
  var activeRequestId = 0; /* Monotonic counter for race condition prevention */
  var hoverTimer = null;
  var hoverRequestId = 0;
  var defaultProductsHtml = '';
  var productsContainer = null;
  var activeLocItem = null;
  var currentLocationResults = [];

  /* -----------------------------------------------------------------------
     Context-aware mode: on loc-type or category collection pages, the
     overlay shows a filtered list of locations instead of global search.
     ----------------------------------------------------------------------- */
  var collectionContext = (window.CRO && window.CRO.collectionContext) || null;

  /* -----------------------------------------------------------------------
     Location type icons (emoji for simplicity, could be SVG)
     ----------------------------------------------------------------------- */
  var typeIcons = {
    'college':         '🎓',
    'military-branch': '🇺🇸'
  };
  var defaultIcon = '🌎';

  var typeLabels = (window.CRO && window.CRO.locationTypeLabels) || {};

  /* -----------------------------------------------------------------------
     Open / Close
     ----------------------------------------------------------------------- */

  /* GA4 event helper — no-op if analytics is absent; must never break search. */
  function track(name, params) {
    try {
      var p = params || {};
      /* GTM-first: this site loads gtag via GTM, where bare gtag(event) calls
         are dropped unless a Google tag config exists. GTM Custom Event
         triggers consume dataLayer.push({event}) — so push that shape. */
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        p.event = name;
        window.dataLayer.push(p);
      } else if (typeof window.gtag === 'function') {
        window.gtag('event', name, p);
      }
    } catch (err) { /* ignore */ }
  }

  function openSearch() {
    overlay.classList.add('cro-search--active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    /* Context-aware placeholder */
    updatePlaceholder();

    setTimeout(function () { input.focus(); }, 50);
    showDefault();
    track('cro_search_opened', { page_path: window.location.pathname });
  }

  function closeSearch() {
    overlay.classList.remove('cro-search--active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    input.value = '';
    currentQuery = '';
    clearTimeout(debounceTimer);
    activeRequestId++; /* Cancel any pending product fetches */
    focusedIndex = -1;
  }

  /* Set initial ARIA state */
  overlay.setAttribute('aria-hidden', 'true');

  /* -----------------------------------------------------------------------
     Live overlay navigation: clicking locations/quicklinks updates the
     page behind the overlay without closing it. Only product links and
     "view all" navigate away normally.
     ----------------------------------------------------------------------- */

  /* Intercept clicks inside the overlay */
  overlay.addEventListener('click', function (e) {
    /* Filter pill dismiss */
    var pill = e.target.closest('.cro-search-filter-pill');
    if (pill) {
      e.preventDefault();
      removePillAndNavigate(pill);
      return;
    }
    var clearAll = e.target.closest('.cro-search-filter-clear');
    if (clearAll) {
      e.preventDefault();
      clearAllAndNavigate();
      return;
    }

    /* Any link inside the overlay → close overlay and let the browser
       navigate normally. This includes product links, "view all", quick
       link pills, location results, and sub-location context links.
       Note: filter pill dismiss (× on active pills) is handled above and
       returns early; that path keeps the overlay open. */
    var link = e.target.closest('a[href]');
    if (link && link.href) {
      track('cro_search_result_click', {
        search_term: currentQuery || '',
        link_url: link.pathname || '',
        link_text: (link.textContent || '').trim().slice(0, 60)
      });
      closeSearch();
      /* Let the default <a> click happen */
    }
  });

  /**
   * Navigate to a new tag-filtered URL without leaving the overlay.
   * Updates pills, context, overlay results, URL, and the collection grid.
   *
   * Breadcrumb logic: if the target location is a child of the current
   * loc-name (via parentStates), keep the parent loc-name as a breadcrumb
   * pill so the user can navigate back up.
   */
  function navigateInOverlay(href) {
    var targetTags = parseTagsFromUrl(href);

    /* Check if target has a loc-name that's a child of the current loc-name */
    if (collectionContext && collectionContext.locName && window.CRO && window.CRO.locationIndex) {
      var targetLocName = '';
      targetTags.forEach(function (t) {
        if (t.indexOf('loc-name:') === 0) targetLocName = t.split(':')[1];
      });

      if (targetLocName && targetLocName !== collectionContext.locName) {
        /* Find the target location in the index */
        var targetLoc = window.CRO.locationIndex.find(function (loc) {
          return loc.slug === targetLocName;
        });

        /* If the target's parentStates includes the current loc-name, it's a child */
        if (targetLoc && targetLoc.parentStates &&
            targetLoc.parentStates.indexOf(collectionContext.locName) > -1) {
          /* Merge: keep current non-loc-name tags + existing loc-name breadcrumbs + new loc-name */
          var filtersEl = document.getElementById('CroSearchFilters');
          var currentPills = filtersEl ? filtersEl.querySelectorAll('.cro-search-filter-pill') : [];
          var mergedTags = [];

          /* Keep all existing pill tags (including parent loc-names) */
          for (var i = 0; i < currentPills.length; i++) {
            var pt = currentPills[i].getAttribute('data-tag') || '';
            if (pt) mergedTags.push(pt);
          }

          /* Add the new loc-name */
          mergedTags.push('loc-name:' + targetLocName);

          setTagsAndRefresh(mergedTags);
          return;
        }
      }
    }

    setTagsAndRefresh(targetTags);
  }

  function removePillAndNavigate(pill) {
    var filtersEl = document.getElementById('CroSearchFilters');
    var dismissedTag = pill.getAttribute('data-tag') || '';

    /* If dismissing a loc-name breadcrumb, also remove all loc-names
       that appear after it (they are children in the breadcrumb chain) */
    var isLocName = dismissedTag.indexOf('loc-name:') === 0;
    var allPills = filtersEl ? Array.prototype.slice.call(filtersEl.querySelectorAll('.cro-search-filter-pill')) : [];
    var pillIdx = allPills.indexOf(pill);

    if (isLocName && pillIdx >= 0) {
      /* Remove this pill and all subsequent loc-name pills */
      for (var i = allPills.length - 1; i >= pillIdx; i--) {
        var t = allPills[i].getAttribute('data-tag') || '';
        if (i === pillIdx || t.indexOf('loc-name:') === 0) {
          allPills[i].parentNode.removeChild(allPills[i]);
        }
      }
    } else {
      pill.parentNode.removeChild(pill);
    }

    /* If no pills remain, remove the entire filter bar */
    var remaining = filtersEl ? filtersEl.querySelectorAll('.cro-search-filter-pill') : [];
    if (remaining.length === 0 && filtersEl) {
      filtersEl.parentNode.removeChild(filtersEl);
    }

    /* Collect remaining tags from pills */
    var tags = [];
    for (var j = 0; j < remaining.length; j++) {
      var rt = remaining[j].getAttribute('data-tag') || '';
      if (rt) tags.push(rt);
    }
    setTagsAndRefresh(tags);
  }

  function clearAllAndNavigate() {
    setTagsAndRefresh([]);
  }

  /**
   * Core function: given a set of tags, update everything:
   * pills UI, context, overlay results, URL, and collection section.
   */
  function setTagsAndRefresh(tags) {
    /* 1. Rebuild pills UI */
    rebuildPills(tags);

    /* 2. Rebuild context */
    rebuildContextFromTags(tags);

    /* 3. Reset search state */
    locNameProductsCache = null;
    locNameFetching = false;
    input.value = '';
    currentQuery = '';
    updatePlaceholder();

    /* 4. Re-render overlay results */
    showDefault();

    /* 5. Update URL */
    var path = buildPathFromTags(tags);
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', path);
    }

    /* 6. Update collection grid behind the overlay */
    fetchAndSwapCollection(path);
  }

  function parseTagsFromUrl(href) {
    /* Extract tags from a URL like /collections/all/category:geography+loc-name:california
       or /collections/all/category-geography+loc-name-california (handleized) */
    var match = href.match(/\/collections\/[^/]+\/(.+)/);
    if (!match) return [];

    var tagStr = match[1].replace(/\?.*$/, ''); /* strip query params */
    var parts = tagStr.split('+');
    var tags = [];

    parts.forEach(function (part) {
      /* Convert handleized format back to colon format:
         "category-geography" → "category:geography"
         "loc-name-california" → "loc-name:california"
         "loc-type-state" → "loc-type:state"
         "product-type-pillow" → "product-type:pillow" */
      var colonTag = part
        .replace(/^(category)-/, '$1:')
        .replace(/^(loc-name)-/, '$1:')
        .replace(/^(loc-type)-/, '$1:')
        .replace(/^(product-type)-/, '$1:');
      tags.push(colonTag);
    });

    return tags;
  }

  function rebuildPills(tags) {
    var filtersEl = document.getElementById('CroSearchFilters');
    var modal = document.querySelector('.cro-search-modal');
    var header = document.querySelector('.cro-search-header');

    /* Only show structured tags as pills (category, loc-type, loc-name) */
    var pillTags = tags.filter(function (t) {
      var prefix = t.split(':')[0];
      return prefix === 'category' || prefix === 'loc-type' || prefix === 'loc-name' || prefix === 'product-type';
    });

    if (pillTags.length === 0) {
      /* Remove filter bar if it exists */
      if (filtersEl) filtersEl.parentNode.removeChild(filtersEl);
      return;
    }

    /* Create or reuse the filter bar */
    if (!filtersEl) {
      filtersEl = document.createElement('div');
      filtersEl.className = 'cro-search-filters';
      filtersEl.id = 'CroSearchFilters';
      /* Insert after the header */
      if (header && header.nextSibling) {
        modal.insertBefore(filtersEl, header.nextSibling);
      } else {
        modal.appendChild(filtersEl);
      }
    }

    /* Build pill HTML */
    var html = '';
    pillTags.forEach(function (tag) {
      var val = tag.split(':')[1] || '';
      var display = titleCase(val);
      html += '<a href="#" class="cro-search-filter-pill" data-tag="' + escapeHtml(tag) + '">' +
        escapeHtml(display) +
        '<span class="cro-search-filter-pill-x">&times;</span></a>';
    });
    html += '<a href="#" class="cro-search-filter-clear">Clear all</a>';
    filtersEl.innerHTML = html;
  }

  function rebuildContextFromTags(tags) {
    var newType = '';
    var newLabel = '';
    var newLocName = '';

    tags.forEach(function (tag) {
      if (tag.indexOf('loc-name:') === 0) {
        newLocName = tag.split(':')[1] || '';
      }
      if (tag === 'category:collegiate') { newType = 'college'; newLabel = 'Colleges'; }
      else if (tag === 'loc-type:state') { newType = 'state'; newLabel = 'States'; }
      else if (tag === 'loc-type:city') { newType = 'city'; newLabel = 'Cities'; }
      else if (tag === 'loc-type:national-park') { newType = 'national-park'; newLabel = 'National Parks'; }
      else if (tag === 'loc-type:ski-resort') { newType = 'ski-resort'; newLabel = 'Ski Resorts'; }
      else if (tag === 'loc-type:country') { newType = 'country'; newLabel = 'World'; }
    });

    if (newLocName) {
      collectionContext = { type: newType, label: newLabel, locName: newLocName };
    } else if (newType) {
      collectionContext = { type: newType, label: newLabel };
    } else {
      collectionContext = null;
    }

    if (window.CRO) window.CRO.collectionContext = collectionContext;
  }

  function updatePlaceholder() {
    if (collectionContext && collectionContext.locName) {
      var locLabel = collectionContext.locName.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      input.placeholder = 'Search ' + locLabel + ' products...';
    } else if (collectionContext) {
      input.placeholder = 'Search ' + collectionContext.label.toLowerCase() + '...';
    } else {
      input.placeholder = 'Search anything...';
    }
  }

  function buildPathFromTags(tags) {
    var match = window.location.pathname.match(/^\/collections\/([^/]+)/);
    var handle = (match && match[1]) || 'all';
    handle = handle.split('/')[0];

    /* For the Shopify URL, only use the LAST loc-name (the most specific).
       Multiple loc-names are breadcrumb-only UI; Shopify AND filtering
       with multiple loc-name tags would return empty results. */
    var lastLocName = '';
    var urlTags = [];
    tags.forEach(function (t) {
      if (t.indexOf('loc-name:') === 0) {
        lastLocName = t;
      } else {
        urlTags.push(t);
      }
    });
    if (lastLocName) urlTags.push(lastLocName);

    var path = '/collections/' + handle;
    if (urlTags.length > 0) {
      path += '/' + urlTags.map(function (t) { return t.replace(/:/g, '-'); }).join('+');
    }
    return path;
  }

  /**
   * Fetch the collection section HTML for the given path and swap it
   * into the page behind the overlay.
   */
  function fetchAndSwapCollection(path) {
    var sectionId = 'cro-collection-universal';
    var url = path + (path.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId;

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var target = document.getElementById('shopify-section-' + sectionId);
        if (!target) return;

        /* The Section Rendering API wraps the response in its own
           <div id="shopify-section-..."> container. Parse out the
           inner content to avoid nesting wrappers. */
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var innerSection = temp.querySelector('[id="shopify-section-' + sectionId + '"]');
        target.innerHTML = innerSection ? innerSection.innerHTML : html;

        /* Re-initialize sub-location sections (Collegiate Designs,
           More [State] Designs) since the old DOM was destroyed. */
        if (window.CRO && window.CRO.initRelatedLocations) {
          window.CRO.initRelatedLocations();
        }
      })
      .catch(function () {
        /* Silently fail — user can still close overlay and the page will
           be on the old content, but the URL is already updated so a
           refresh will show the correct page. */
      });
  }

  function titleCase(str) {
    return str.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  /* Bind open triggers — any element with data-cro-search-open */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cro-search-open]');
    if (trigger) {
      e.preventDefault();
      openSearch();
    }
  });

  /* Close button */
  if (closeBtn) {
    closeBtn.addEventListener('click', closeSearch);
  }

  /* Click outside modal to close */
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closeSearch();
  });

  /* Escape key to close */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.classList.contains('cro-search--active')) {
      closeSearch();
    }
    /* Cmd/Ctrl+K to open */
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      if (overlay.classList.contains('cro-search--active')) {
        closeSearch();
      } else {
        openSearch();
      }
    }
  });

  /* -----------------------------------------------------------------------
     Search logic
     ----------------------------------------------------------------------- */
  input.addEventListener('input', function () {
    var q = input.value.trim();

    if (q.length === 0) {
      currentQuery = '';
      showDefault();
      return;
    }

    currentQuery = q;

    /* Location-scoped mode: search within a specific location's products */
    if (collectionContext && collectionContext.locName) {
      showLocNameProducts(q);
      return;
    }

    /* Context-aware mode: filter within the current location type */
    if (collectionContext) {
      showContextList(q);
      return;
    }

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      performSearch(q);
    }, q.length < 3 ? 200 : 150);
  });

  /* Form submit — go to Shopify search results page */
  if (form) {
    form.addEventListener('submit', function () {
      closeSearch();
    });
  }

  /* -----------------------------------------------------------------------
     Display states
     ----------------------------------------------------------------------- */
  function showDefault() {
    if (collectionContext && collectionContext.locName) {
      showLocNameProducts('');
      return;
    }
    if (collectionContext) {
      showContextList('');
      return;
    }
    defaultEl.style.display = '';
    resultsEl.style.display = 'none';
    emptyEl.style.display = 'none';
    bodyEl.classList.remove('cro-search-body--has-split');
    focusedIndex = -1;
    allResultLinks = [];
  }

  function showResults(html) {
    defaultEl.style.display = 'none';
    resultsEl.style.display = '';
    emptyEl.style.display = 'none';
    bodyEl.classList.add('cro-search-body--has-split');
    resultsEl.innerHTML = html;
    focusedIndex = -1;
    refreshResultLinks();
  }

  function showEmpty() {
    defaultEl.style.display = 'none';
    resultsEl.style.display = 'none';
    emptyEl.style.display = '';
    bodyEl.classList.remove('cro-search-body--has-split');
    /* Populate the current query into the empty state so the user sees
       "No results for <their query>" rather than a generic message. */
    var q = input && input.value ? input.value.trim() : '';
    var qSlot = document.getElementById('CroSearchEmptyQuery');
    if (qSlot) qSlot.textContent = q;
    focusedIndex = -1;
    allResultLinks = [];
    if (q) track('cro_search_empty', { search_term: q });
  }

  /* -----------------------------------------------------------------------
     Context-aware list: shows all locations of a given type, optionally
     filtered by a search query. No products panel — just a clean list.
     ----------------------------------------------------------------------- */
  function showContextList(query) {
    if (!collectionContext || !window.CRO || !window.CRO.locationIndex) {
      showDefault();
      return;
    }

    var type = collectionContext.type;
    var label = collectionContext.label;
    var q = query.toLowerCase();

    /* Filter locations by type */
    var locs = window.CRO.locationIndex.filter(function (loc) {
      return loc.type === type;
    });

    /* If there's a search query, further filter by name match */
    if (q) {
      locs = locs.filter(function (loc) {
        return loc.name.toLowerCase().indexOf(q) > -1 ||
               loc.slug.indexOf(q.replace(/\s+/g, '-')) > -1;
      });

      /* Sort: prefix matches first, then alphabetical */
      locs.sort(function (a, b) {
        var aName = a.name.toLowerCase();
        var bName = b.name.toLowerCase();
        var aPrefix = aName.indexOf(q) === 0 ? 1 : 0;
        var bPrefix = bName.indexOf(q) === 0 ? 1 : 0;
        if (bPrefix !== aPrefix) return bPrefix - aPrefix;
        return aName.localeCompare(bName);
      });
    } else {
      /* No query — sort alphabetically */
      locs.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });
    }

    if (locs.length === 0) {
      showEmpty();
      return;
    }

    /* Build a clean, single-column list of names — no icons, no subtitles */
    var html = '<ul class="cro-search-context-results" role="listbox">';

    locs.forEach(function (loc) {
      var url = buildLocationUrl(loc);
      var name = q ? highlightMatch(loc.name, query) : escapeHtml(loc.name);
      html += '<li class="cro-search-context-item" role="option">' +
        '<a href="' + escapeHtml(url) + '" class="cro-search-context-link">' +
        name + '</a></li>';
    });

    html += '</ul>';

    /* Show in results container (no split layout) */
    defaultEl.style.display = 'none';
    resultsEl.style.display = '';
    emptyEl.style.display = 'none';
    bodyEl.classList.remove('cro-search-body--has-split');
    resultsEl.innerHTML = html;
    focusedIndex = -1;
    refreshResultLinks();
  }

  /* -----------------------------------------------------------------------
     Location-scoped product search: when on a specific loc-name page
     (e.g., /collections/all/loc-name:texas), fetch all products for that
     location and filter by title client-side.
     ----------------------------------------------------------------------- */
  var locNameProductsCache = null; /* Cache fetched products for the session */
  var locNameFetching = false;

  function showLocNameProducts(query) {
    if (!collectionContext || !collectionContext.locName) return;

    var q = query.toLowerCase();

    /* If we have cached products, filter and display immediately */
    if (locNameProductsCache) {
      renderLocNameResults(locNameProductsCache, q);
      return;
    }

    /* Show loading on first open */
    if (locNameFetching) return; /* Already fetching */
    locNameFetching = true;

    defaultEl.style.display = 'none';
    resultsEl.style.display = '';
    emptyEl.style.display = 'none';
    bodyEl.classList.remove('cro-search-body--has-split');
    resultsEl.innerHTML = '<p class="cro-search-loading">Loading products…</p>';

    /* Fetch all products for this location via Section Rendering API */
    var url = '/collections/all/loc-name:' + collectionContext.locName +
      '?section_id=cro-search-products-json';

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var jsonStr = html.replace(/^[^[]*/, '').replace(/][^]*$/, ']');
        var raw = [];
        try { raw = JSON.parse(jsonStr) || []; } catch (e) { raw = []; }
        locNameProductsCache = raw.map(function (p) {
          return {
            title: p.title || '',
            url: p.url || '#',
            image: p.image || '',
            price: p.price || '',
            price_min: p.price_min || '',
            price_varies: !!p.price_varies
          };
        });
        locNameFetching = false;
        /* Re-render with current query (user may have typed while loading) */
        renderLocNameResults(locNameProductsCache, input.value.trim().toLowerCase());
      })
      .catch(function () {
        locNameFetching = false;
        locNameProductsCache = [];
        showEmpty();
      });
  }

  function renderLocNameResults(products, query) {
    var slug = collectionContext.locName;

    /* Sub-locations: find locations whose parentStates includes this loc-name */
    var subLocs = [];
    if (window.CRO && window.CRO.locationIndex) {
      subLocs = window.CRO.locationIndex.filter(function (loc) {
        return loc.parentStates && loc.parentStates.indexOf(slug) > -1;
      });

      /* Filter sub-locations by query */
      if (query) {
        subLocs = subLocs.filter(function (loc) {
          return loc.name.toLowerCase().indexOf(query) > -1 ||
                 loc.slug.indexOf(query.replace(/\s+/g, '-')) > -1;
        });
      }

      /* Sort: prefix matches first, then alphabetical */
      subLocs.sort(function (a, b) {
        if (query) {
          var aName = a.name.toLowerCase();
          var bName = b.name.toLowerCase();
          var aPrefix = aName.indexOf(query) === 0 ? 1 : 0;
          var bPrefix = bName.indexOf(query) === 0 ? 1 : 0;
          if (bPrefix !== aPrefix) return bPrefix - aPrefix;
        }
        return a.name.localeCompare(b.name);
      });
    }

    /* Products: filter by title */
    var filtered = products;
    if (query) {
      filtered = products.filter(function (p) {
        return p.title.toLowerCase().indexOf(query) > -1;
      });
    }

    var hasSubLocs = subLocs.length > 0;
    var hasProducts = filtered.length > 0;

    if (!hasSubLocs && !hasProducts) {
      showEmpty();
      return;
    }

    /* Use the same split layout as performSearch so hover-to-preview works */
    if (hasSubLocs) {
      /* Reset hover state */
      clearTimeout(hoverTimer);
      hoverRequestId++;
      activeLocItem = null;
      currentLocationResults = subLocs;

      /* Build locations HTML (left column) */
      var locationHtml = '<div class="cro-search-section cro-search-section--locations">';
      locationHtml += '<ul class="cro-search-results-list" role="listbox">';
      subLocs.forEach(function (loc, idx) {
        locationHtml += renderLocationResult(loc, query || '', idx);
      });
      locationHtml += '</ul></div>';

      /* Build products HTML (right column) */
      var productsHtml = buildProductsHtml(filtered);
      defaultProductsHtml = productsHtml;

      var fullHtml = locationHtml;
      fullHtml += '<div class="cro-search-section cro-search-section--products" id="CroSearchProducts">';
      fullHtml += productsHtml;
      fullHtml += '</div>';

      showResults(fullHtml);
      productsContainer = document.getElementById('CroSearchProducts');

      /* Bind hover handlers so hovering a sub-location previews its products */
      bindLocationHover(subLocs);
    } else {
      /* No sub-locations — just show filtered products in a flat list */
      var html = '<div class="cro-search-section">';
      html += '<ul class="cro-search-products-list">';
      filtered.forEach(function (p) {
        html += renderProductResult(p);
      });
      html += '</ul></div>';

      defaultEl.style.display = 'none';
      resultsEl.style.display = '';
      emptyEl.style.display = 'none';
      bodyEl.classList.remove('cro-search-body--has-split');
      resultsEl.innerHTML = html;
      focusedIndex = -1;
      refreshResultLinks();
    }
  }

  /* -----------------------------------------------------------------------
     Core search: location index + Shopify API
     ----------------------------------------------------------------------- */
  function performSearch(query) {
    if (query && query.length >= 2) {
      track('cro_search_query', { search_term: query, page_path: window.location.pathname });
    }
    var locationResults = searchLocations(query);
    var hasLocations = locationResults.length > 0;

    /* Reset hover state */
    defaultProductsHtml = '';
    productsContainer = null;
    activeLocItem = null;
    currentLocationResults = locationResults;
    clearTimeout(hoverTimer);
    hoverRequestId++;

    /* Increment request ID — any callback with an older ID is stale */
    var requestId = ++activeRequestId;

    /* Build location HTML once (reused in temp and final render) */
    var locationHtml = '';
    if (hasLocations) {
      locationHtml += '<div class="cro-search-section cro-search-section--locations">';
      locationHtml += '<h3 class="cro-search-section-title">Places & Colleges</h3>';
      locationHtml += '<ul class="cro-search-results-list" role="listbox">';
      locationResults.forEach(function (loc, idx) {
        locationHtml += renderLocationResult(loc, query, idx);
      });
      locationHtml += '</ul></div>';
    }

    /* Show location results immediately + loading indicator for products */
    var tempHtml = locationHtml;
    tempHtml += '<div class="cro-search-section cro-search-section--products" id="CroSearchProducts">';
    tempHtml += '<p class="cro-search-loading">Searching products…</p>';
    tempHtml += '</div>';

    /* View all link */
    tempHtml += '<div class="cro-search-section cro-search-section--full">';
    tempHtml += '<a href="/search?q=' + encodeURIComponent(query) + '&type=product" class="cro-search-view-all">';
    tempHtml += 'View all results for &ldquo;' + escapeHtml(query) + '&rdquo;';
    tempHtml += '</a></div>';

    showResults(tempHtml);
    productsContainer = document.getElementById('CroSearchProducts');

    /* Bind hover handlers on location items */
    if (hasLocations) {
      bindLocationHover(locationResults);
    }

    /* Fetch product results from Shopify in parallel */
    fetchProducts(query, function (products) {
      /* Discard stale responses */
      if (requestId !== activeRequestId) return;

      var hasProducts = products && products.length > 0;

      if (!hasLocations && !hasProducts) {
        showEmpty();
        return;
      }

      /* Build and store the default products HTML */
      defaultProductsHtml = buildProductsHtml(products);

      /* Only update products if no location is currently hovered */
      if (!activeLocItem && productsContainer) {
        productsContainer.innerHTML = defaultProductsHtml;
        refreshResultLinks();
      }
    });
  }

  function buildProductsHtml(products) {
    if (!products || products.length === 0) return '';
    var html = '<h3 class="cro-search-section-title">Products</h3>';
    html += '<ul class="cro-search-products-list">';
    products.forEach(function (p) {
      html += renderProductResult(p);
    });
    html += '</ul>';
    return html;
  }

  /* -----------------------------------------------------------------------
     Location hover → update products
     ----------------------------------------------------------------------- */
  function bindLocationHover(locationResults) {
    var items = resultsEl.querySelectorAll('.cro-search-result-item');
    var locList = resultsEl.querySelector('.cro-search-results-list');

    items.forEach(function (item, idx) {
      var loc = locationResults[idx];
      if (!loc) return;

      item.addEventListener('mouseenter', function () {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          setActiveLocation(item, loc);
        }, 150);
      });
    });

    /* Mouse leaves the entire locations list → restore defaults */
    if (locList) {
      locList.addEventListener('mouseleave', function () {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          clearActiveLocation();
        }, 100);
      });
    }
  }

  function setActiveLocation(item, loc) {
    /* Highlight the active location */
    if (activeLocItem) activeLocItem.classList.remove('cro-search-result-item--active');
    item.classList.add('cro-search-result-item--active');
    activeLocItem = item;

    if (!productsContainer) return;

    /* Show loading state */
    productsContainer.classList.add('cro-search-section--loading');

    /* Fetch products for this location by tag (not fuzzy text search)
       so "New York State" doesn't fuzzy-match NYC/NYU products. */
    var rid = ++hoverRequestId;
    fetchProductsByLocation(loc, function (products) {
      if (rid !== hoverRequestId) return;
      if (!productsContainer) return;
      productsContainer.classList.remove('cro-search-section--loading');

      var html = buildProductsHtml(products);
      if (!html) {
        html = '<p class="cro-search-products-empty">No products for ' + escapeHtml(loc.name) + '</p>';
      }
      productsContainer.innerHTML = html;
      refreshResultLinks();
    });
  }

  function clearActiveLocation() {
    if (activeLocItem) {
      activeLocItem.classList.remove('cro-search-result-item--active');
      activeLocItem = null;
    }
    if (productsContainer && defaultProductsHtml) {
      productsContainer.classList.remove('cro-search-section--loading');
      productsContainer.innerHTML = defaultProductsHtml;
      refreshResultLinks();
    }
  }

  function refreshResultLinks() {
    allResultLinks = Array.prototype.slice.call(
      resultsEl.querySelectorAll('.cro-search-result-link, .cro-search-product-link, .cro-search-context-link')
    );
  }

  /* -----------------------------------------------------------------------
     Location index search
     ----------------------------------------------------------------------- */
  function searchLocations(query) {
    if (!window.CRO || !window.CRO.locationIndex) return [];

    var q = query.toLowerCase();
    var results = [];

    window.CRO.locationIndex.forEach(function (loc) {
      var name = loc.name.toLowerCase();
      var slug = loc.slug;
      var score = 0;

      if (name === q) {
        score = 100;
      } else if (name.indexOf(q) === 0) {
        score = 80;
      } else if (slug.indexOf(q) === 0) {
        score = 75;
      } else if (name.indexOf(q) > 0) {
        score = 50;
      } else if (slug.indexOf(q.replace(/\s+/g, '-')) > -1) {
        score = 40;
      }

      /* Also match parent states */
      if (score === 0 && loc.parentStates) {
        for (var i = 0; i < loc.parentStates.length; i++) {
          if (loc.parentStates[i].indexOf(q) === 0) {
            score = 30;
            break;
          }
        }
      }

      if (score > 0) {
        results.push({ loc: loc, score: score });
      }
    });

    /* Sort by score desc, then alphabetically */
    results.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.loc.name.localeCompare(b.loc.name);
    });

    return results.slice(0, 8).map(function (r) { return r.loc; });
  }

  /* -----------------------------------------------------------------------
     Shopify Predictive Search API
     ----------------------------------------------------------------------- */
  function fetchProducts(query, callback) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=4' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,variants.title,tag';

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = [];
        if (data.resources && data.resources.results && data.resources.results.products) {
          products = data.resources.results.products;
        }
        callback(products);
      })
      .catch(function () {
        callback([]);
      });
  }

  /* Fetch products for a specific location using Shopify's Section
     Rendering API. This is precise (returns only products matching the
     loc-name tag) and works identically on dev and production.

     The request hits:
       /collections/all/loc-name:<slug>?section_id=cro-search-products-json

     Shopify renders sections/cro-search-products-json.liquid in the
     context of the tag-filtered collection and returns the output wrapped
     in a <div>. We strip the wrapper and parse the JSON inside. */
  function fetchProductsByLocation(loc, callback) {
    if (!loc || !loc.slug) {
      callback([]);
      return;
    }
    var url = '/collections/all/loc-name:' + loc.slug +
      '?section_id=cro-search-products-json';

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        /* Shopify wraps section output in a <div> — strip it to get the
           JSON array. Match the content between > and </div>. */
        var jsonStr = html.replace(/^[^[]*/, '').replace(/][^]*$/, ']');
        var raw = [];
        try { raw = JSON.parse(jsonStr) || []; } catch (e) { raw = []; }
        var normalized = raw.map(function (p) {
          return {
            title: p.title || '',
            url: p.url || '#',
            image: p.image || '',
            price: p.price || '',
            price_min: p.price_min || '',
            price_varies: !!p.price_varies
          };
        });
        callback(normalized);
      })
      .catch(function () {
        callback([]);
      });
  }

  /* -----------------------------------------------------------------------
     Rendering
     ----------------------------------------------------------------------- */
  function buildLocationUrl(loc) {
    /*
     * Build a tag-filtered collection URL for this location.
     * Use colon format (loc-name:california) not handleized (loc-name-california)
     * because handleized is ambiguous when values contain hyphens
     * (e.g. loc-name-new-york-state could be loc-name:new-york-state or loc-name-new:york-state).
     *
     * If the entry has a `customUrl` field, use it directly. This is used for
     * things like military branches that don't have their own loc-name tag
     * on products yet — we fall back to the category filter.
     */
    if (loc.customUrl) return loc.customUrl;
    var tags = [];
    if (loc.category) tags.push('category:' + loc.category);
    tags.push('loc-name:' + loc.slug);
    return '/collections/all/' + tags.join('+');
  }

  function renderLocationResult(loc, query, idx) {
    var url = buildLocationUrl(loc);
    var icon = typeIcons[loc.type] || defaultIcon;
    var label = typeLabels[loc.type] || loc.type;
    var name = highlightMatch(loc.name, query);
    var meta = escapeHtml(label);

    if (loc.parentStates && loc.parentStates.length > 0 && loc.parentStates.length <= 2) {
      meta += ' &middot; ' + loc.parentStates.map(function (s) {
        var formatted = s.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        return escapeHtml(formatted);
      }).join(', ');
    }

    return '<li class="cro-search-result-item" role="option">' +
      '<a href="' + escapeHtml(url) + '" class="cro-search-result-link">' +
      '<span class="cro-search-result-icon cro-search-result-icon--' + loc.type + '">' + icon + '</span>' +
      '<span class="cro-search-result-text">' +
      '<span class="cro-search-result-name">' + name + '</span>' +
      '<span class="cro-search-result-meta">' + meta + '</span>' +
      '</span>' +
      '<span class="cro-search-result-arrow">&rarr;</span>' +
      '</a></li>';
  }

  function renderProductResult(product) {
    if (!product) return '';
    var image = product.image || (product.featured_image && product.featured_image.url) || '';
    var title = product.title || '';
    var price = '';
    /* Shopify Predictive Search API (/search/suggest.json) and our JSON
       alternate view both return prices as decimal-dollar strings
       (e.g. "24.95"), NOT as cents. Do not divide. */
    if (product.price_varies && product.price_min) {
      price = 'From $' + parseFloat(product.price_min).toFixed(2);
    } else if (product.price) {
      price = '$' + parseFloat(product.price).toFixed(2);
    } else if (product.price_min) {
      price = 'From $' + parseFloat(product.price_min).toFixed(2);
    }
    var url = product.url || '#';

    return '<li class="cro-search-product-item">' +
      '<a href="' + escapeHtml(url) + '" class="cro-search-product-link">' +
      (image ? '<img class="cro-search-product-thumb" src="' + escapeHtml(getSizedImage(image, '80x80')) + '" alt="' + escapeHtml(title) + '" loading="lazy" width="40" height="40" />' : '') +
      '<span class="cro-search-product-text">' +
      '<span class="cro-search-product-title">' + escapeHtml(title) + '</span>' +
      (price ? '<span class="cro-search-product-price">' + escapeHtml(price) + '</span>' : '') +
      '</span>' +
      '</a></li>';
  }

  /* -----------------------------------------------------------------------
     Keyboard navigation
     ----------------------------------------------------------------------- */
  input.addEventListener('keydown', function (e) {
    if (allResultLinks.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusedIndex = Math.min(focusedIndex + 1, allResultLinks.length - 1);
      updateFocus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusedIndex = Math.max(focusedIndex - 1, -1);
      updateFocus();
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      var link = allResultLinks[focusedIndex];
      if (link && link.href) {
        closeSearch();
        window.location.href = link.href;
      }
    }
  });

  function updateFocus() {
    allResultLinks.forEach(function (el, i) {
      if (i === focusedIndex) {
        el.classList.add('cro-search--focused');
        el.scrollIntoView({ block: 'nearest' });
      } else {
        el.classList.remove('cro-search--focused');
      }
    });

    /* Update products to match focused location */
    if (focusedIndex >= 0) {
      var focusedLink = allResultLinks[focusedIndex];
      var locItem = focusedLink ? focusedLink.closest('.cro-search-result-item') : null;
      if (locItem) {
        /* Find which location index this item corresponds to */
        var locItems = Array.prototype.slice.call(
          resultsEl.querySelectorAll('.cro-search-result-item')
        );
        var locIdx = locItems.indexOf(locItem);
        if (locIdx >= 0 && currentLocationResults[locIdx]) {
          clearTimeout(hoverTimer);
          setActiveLocation(locItem, currentLocationResults[locIdx]);
        }
      } else {
        /* Focused on a product link — clear active location highlight but keep products */
        if (activeLocItem) {
          activeLocItem.classList.remove('cro-search-result-item--active');
          activeLocItem = null;
        }
      }
    } else {
      /* Focus returned to input (-1) — restore defaults */
      clearTimeout(hoverTimer);
      clearActiveLocation();
    }
  }

  /* -----------------------------------------------------------------------
     Helpers
     ----------------------------------------------------------------------- */
  function highlightMatch(text, query) {
    if (!query) return escapeHtml(text);
    var idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return escapeHtml(text);
    return escapeHtml(text.substring(0, idx)) +
      '<mark>' + escapeHtml(text.substring(idx, idx + query.length)) + '</mark>' +
      escapeHtml(text.substring(idx + query.length));
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function handleize(str) {
    /* Match Shopify's handle filter: lowercase, replace non-alphanumeric with hyphens, collapse, trim */
    return str.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  function getSizedImage(url, size) {
    /* Shopify image URL sizing: insert _SIZEx before extension */
    if (!url || typeof url !== 'string') return '';
    /* Avoid double-sizing if already sized */
    if (url.indexOf('_' + size) > -1) return url;
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/, '_' + size + '.$1$2');
  }

})();
